import { baseApi } from './baseApi'
import { Totals, ReportFilter, OrderInfo } from 'ts-back-end/src/reporting/types/one-ship-report'
import {
  BankDepositPaymentMethod,
  BankDepositReport,
} from 'ts-back-end/src/reporting/types/bank-deposit-report'
import {
  PreferenceSetupEntityType,
  PreferenceSetupReport,
} from 'ts-back-end/src/reporting/types/preference-setup-report'
import { WriteOffReport } from 'ts-back-end/src/reporting/types/write-off-report'
import {
  FollowUpCareReport,
  FollowUpCareReportProviderType,
  FollowUpCareReportStatus,
  PatientAgingReport,
  PatientAgingType,
} from 'store/types'

export type AuditTrailItem = {
  id: number
  timestamp: string
  userId: number
  userName: string
  userEmail: string
  orderId: number
  action: string
  documentName: string | null
  activitySource: string
  ipAddress: string | null
  userAgent: string | null
}

export type AuditTrailResponse = {
  totalItems: number
  totalPages: number
  currentPage: number
  items: AuditTrailItem[]
}

export type AuditTrailParams = {
  page: number
  size: number
  from?: string
  to?: string
  userId?: number
  orderId?: number
  patientId?: number
}

interface OneShipResult {
  totals: Totals
  filters: ReportFilter
  orders: OrderInfo[]
}

interface OneShipQuery {
  billedFrom?: string
  billedTo?: string
  createdFrom?: string
  createdTo?: string
  companyIds?: string[]
  officeIds?: string[]
  shippedBackFrom?: string
  shippedBackTo?: string
  shippedFrom?: string
  shippedTo?: string
}

interface DailyCountsQuery {
  from: string
  to: string
  companyIds?: string[]
  dateMode: string
  groupBy: string
}

type PatientAgingQuery = {
  type: PatientAgingType
  status: string
  page?: number
  size?: number
}

type PreferenceSetupReportQuery = {
  page?: number
  size?: number
  entityType?: PreferenceSetupEntityType
  search?: string
}

type BankDepositReportQuery = {
  page?: number
  size?: number
  paymentDateFrom?: string
  paymentDateTo?: string
  paymentMethods?: BankDepositPaymentMethod[]
  patientId?: number
  payerId?: number
  billingLocation?: string
  search?: string
}

export type WriteOffReportQuery = {
  page?: number
  size?: number
  from?: string
  to?: string
  payerId?: number
  category?: string
  reason?: string
}

type FollowUpCareReportQuery = {
  page?: number
  size?: number
  officeId?: number
  referringProviderId?: number
  followUpProviderId?: number
  status?: FollowUpCareReportStatus
  followUpProviderType?: FollowUpCareReportProviderType
  patientFirstName?: string
  patientLastName?: string
}

export enum ProductivityMetric {
  VERIFIED_FIELDS = 'VERIFIED_FIELDS',
  NOTES_MESSAGES = 'NOTES_MESSAGES',
  ORDERS_CREATED = 'ORDERS_CREATED',
  DOCUMENTS_ATTACHED = 'DOCUMENTS_ATTACHED',
  LABELS_PRINTED = 'LABELS_PRINTED',
  DEVICE_ASSIGNMENTS = 'DEVICE_ASSIGNMENTS',
  REPORTS_UPLOADED = 'REPORTS_UPLOADED',
  REPORTS_APPROVED = 'REPORTS_APPROVED',
  REPORTS_MODIFIED = 'REPORTS_MODIFIED',
  FAX_CANCELLED_RESENT = 'FAX_CANCELLED_RESENT',
}

export type ProductivityBreakdown = {
  label: string
  count: number
}

export type ProductivityMetricCount = {
  metric: ProductivityMetric
  label: string
  count: number
  breakdown?: ProductivityBreakdown[]
}

export type ProductivitySummary = {
  userId: number
  from?: string
  to?: string
  metrics: ProductivityMetricCount[]
}

export type ProductivityDetailItem = {
  orderId: number | null
  timestamp: string
  section?: string | null
  result?: 'GOOD' | 'FAIL' | null
  reason?: string | null
  text?: string | null
  entryType?: 'note' | 'message' | null
  noteKind?: string | null
  patientName?: string | null
  uploadType?: string | null
  trackingNumber?: string | null
  returnTrackingNumber?: string | null
  testGroup?: 'POX' | 'HST' | null
  deviceId?: number | null
  action?: string | null
  ahi?: number | null
  hstReportType?: string | null
  dateOfService?: string | null
  nights?: number | null
  reportIds?: string | null
}

export type ProductivityCursor = {
  timestamp: string
  eventId: string
}

export type ProductivityDetailsResponse = {
  items: ProductivityDetailItem[]
  pageSize: number
  hasMore: boolean
  nextCursor: ProductivityCursor | null
}

export type ProductivitySummaryParams = {
  userId: number
  from?: string
  to?: string
}

export type ProductivityDetailsParams = ProductivitySummaryParams & {
  metric: ProductivityMetric
  size?: number
  cursorTimestamp?: string
  cursorEventId?: string
}

export const ticketsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOneShipCounts: builder.query<OneShipResult, OneShipQuery>({
      query: (params) => ({ url: `/management-reporting/one-ship`, params }),
      providesTags: ['OneShipCounts'],
    }),
    getDailyCounts: builder.query<any, DailyCountsQuery>({
      query: (params) => ({ url: `/management-reporting/daily`, params }),
      providesTags: ['DailyCounts'],
    }),
    getQuickSightEmbedUrl: builder.query<
      { url: string },
      { dashboardId: string; userType: 'LAB' | 'DME' }
    >({
      query: ({ dashboardId, userType }) => ({
        url: `/${userType === 'LAB' ? 'management-reporting' : 'dme'}/analytics/dashboard/${dashboardId}/embedding-url`,
      }),
    }),
    getPatientAgingReport: builder.query<PatientAgingReport, PatientAgingQuery>({
      query: (params) => ({
        url: `/management-reporting/patient-aging`,
        params,
      }),
    }),
    exportPatientAgingReport: builder.mutation<Blob, Omit<PatientAgingQuery, 'page' | 'size'> & { timeZone: string }>({
      query: (params) => ({
        url: `/management-reporting/patient-aging/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getFollowUpCareReport: builder.query<FollowUpCareReport, FollowUpCareReportQuery>({
      query: (params) => ({
        url: `/management-reporting/follow-up-care`,
        params,
      }),
      providesTags: ['OrderList'],
    }),
    exportFollowUpCareReport: builder.mutation<Blob, Omit<FollowUpCareReportQuery, 'page' | 'size'>>({
      query: (params) => ({
        url: `/management-reporting/follow-up-care/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getPreferenceSetupReport: builder.query<PreferenceSetupReport, PreferenceSetupReportQuery>({
      query: (params) => ({
        url: `/management-reporting/preference-setup`,
        params,
      }),
      providesTags: ['Company', 'Physicians'],
    }),
    exportPreferenceSetupReport: builder.mutation<
      Blob,
      Omit<PreferenceSetupReportQuery, 'page' | 'size'>
    >({
      query: (params) => ({
        url: `/management-reporting/preference-setup/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getBankDepositReport: builder.query<BankDepositReport, BankDepositReportQuery>({
      query: (params) => ({
        url: `/management-reporting/bank-deposit`,
        params,
      }),
      providesTags: ['BillingPatientPayments'],
    }),
    exportBankDepositReport: builder.mutation<
      Blob,
      Omit<BankDepositReportQuery, 'page' | 'size'>
    >({
      query: (params) => ({
        url: `/management-reporting/bank-deposit/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    exportBankDepositReportPdf: builder.mutation<
      Blob,
      Omit<BankDepositReportQuery, 'page' | 'size'>
    >({
      query: (params) => ({
        url: `/management-reporting/bank-deposit/export-pdf`,
        params,
        headers: {
          Accept: 'application/pdf',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getWriteOffReport: builder.query<WriteOffReport, WriteOffReportQuery>({
      query: (params) => ({
        url: `/management-reporting/write-offs`,
        params,
      }),
      providesTags: ['BillingPatientPayments'],
    }),
    exportWriteOffReport: builder.mutation<Blob, Omit<WriteOffReportQuery, 'page' | 'size'> & { timeZone: string }>({
      query: (params) => ({
        url: `/management-reporting/write-offs/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getAuditTrail: builder.query<AuditTrailResponse, AuditTrailParams>({
      query: (params) => ({
        url: `/management-reporting/audit-trail`,
        params,
      }),
    }),
    getProductivitySummary: builder.query<ProductivitySummary, ProductivitySummaryParams>({
      query: (params) => ({
        url: `/management-reporting/productivity/summary`,
        params,
      }),
    }),
    getProductivityDetails: builder.query<ProductivityDetailsResponse, ProductivityDetailsParams>({
      query: (params) => ({
        url: `/management-reporting/productivity/details`,
        params,
      }),
    }),
    exportProductivityDetails: builder.mutation<Blob, Omit<ProductivityDetailsParams, 'page' | 'size'>>({
      query: (params) => ({
        url: `/management-reporting/productivity/details/export`,
        params,
        headers: {
          Accept: 'text/csv',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useGetOneShipCountsQuery,
  useLazyGetOneShipCountsQuery,
  useGetDailyCountsQuery,
  useLazyGetDailyCountsQuery,
  useGetQuickSightEmbedUrlQuery,
  useLazyGetQuickSightEmbedUrlQuery,
  useGetPatientAgingReportQuery,
  useExportPatientAgingReportMutation,
  useGetFollowUpCareReportQuery,
  useExportFollowUpCareReportMutation,
  useGetPreferenceSetupReportQuery,
  useExportPreferenceSetupReportMutation,
  useGetBankDepositReportQuery,
  useExportBankDepositReportMutation,
  useExportBankDepositReportPdfMutation,
  useGetWriteOffReportQuery,
  useExportWriteOffReportMutation,
  useLazyGetAuditTrailQuery,
  useGetProductivitySummaryQuery,
  useLazyGetProductivitySummaryQuery,
  useLazyGetProductivityDetailsQuery,
  useExportProductivityDetailsMutation,
} = ticketsApi
